import {Component, DoCheck, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {NewSimulationService} from "../new-simulation/new-simulation.service";
import {IMyDrpOptions} from "mydaterangepicker";
import {MEAPeriodEntity} from "../../entities/mea-period-entity";
import {isArray} from "util";

@Component({
    selector: 'app-edit-simulation',
    templateUrl: './edit-simulation.component.html',
    styleUrls: ['./edit-simulation.component.css']
})
export class EditSimulationComponent implements OnInit, DoCheck {

    @Input('data')
    public sectionData;


    @Input('changed')
    changed = false;
    @Output()
    changedChange = new EventEmitter();


    public regions = [];
    public countries = [];

    private defaultCurrencyies = [
        {
            Currency_code: 'EUR'
        },
        {
            Currency_code: 'USD'
        }
    ];

    public myDateRangePickerOptions: IMyDrpOptions = {
        dateFormat: 'dd.mm.yyyy',
    };

    public currencies = [];

    public productInformationForm = this.formBuilder.group({
        brandName: ['', Validators.required],
        activeIngredients: ['', Validators.required],
        atcCode: [''],
        strength: this.formBuilder.group({
            value: ['', Validators.required],
            unit: ['', Validators.required]
        }),
        packSize: [''],
        form: ['']
    });

    public meaForm = this.formBuilder.group({
        region: [''],
        country: [''],
        contractingPartner: [''],
        currency: [''],
        startEndDate: [''],
        numberOfMEAPeriods: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
        agreementType: ['', Validators.required]
    });

    public simulationInputForm = this.formBuilder.group({
        scenarioDescription: [''],
        periods: this.formBuilder.array([])
    });

    constructor(private formBuilder: FormBuilder, private simulationService: NewSimulationService) {

        const that = this;

        this.simulationService.fetchRegions()
            .subscribe((regions) => {
                that.regions = regions
                that.onRegionSelect(that.sectionData.data['region']);
            }, error => {
                that.setFormListeners();
            });
    }

    ngOnInit() {

        const startDate = new Date(this.sectionData.data['Expected_start_date']);
        const endDate = new Date(this.sectionData.data['Expected_end_date']);

        this.sectionData.data['startEndDate'] = {
            beginDate: {
                year: startDate.getFullYear(),
                month: startDate.getMonth(),
                day: startDate.getDate()
            },
            endDate: {
                year: endDate.getFullYear(),
                month: endDate.getMonth(),
                day: endDate.getDate()
            }
        };

        this.initSectionFormData();

    }

    ngDoCheck() {
        this.changedChange.next(this.changed);
    }

    onRegionSelect(region) {
        const that = this;

        this.simulationService.fetchCountriesByRegion(region)
            .subscribe((countries) => {
                that.countries = countries;
                that.onCountrySelect(that.sectionData.data['Country_name']);
            }, error => {
                that.setFormListeners();
            });
    }

    public onCountrySelect(country) {

        let that = this;

        this.currencies = [];
        this.currencies = this.currencies.concat(this.defaultCurrencyies);

        this.simulationService.fetchCurrencyByCountry(country)
            .subscribe((currencies) => {
                for (let currency of currencies) {
                    if (!that.currencies.find((x) => {
                            return x.Currency_code === currency.Currency_code;
                        })) {
                        that.currencies.push(currency);
                    }

                }
                that.setFormListeners();
            }, (error) => {
                that.setFormListeners();
            });
    }

    setFormListeners() {

        const that = this;
        this.productInformationForm.valueChanges
            .subscribe((change) => {
                that.changed = true;
            });

        this.meaForm.valueChanges
            .subscribe((change) => {
                that.changed = true;
            });

        this.simulationInputForm.valueChanges
            .subscribe((change) => {
                that.changed = true;
            });
    }

    public onSimulationTypeChange() {
        this.initSectionFormData();
    }

    private generateMEAPeriod(discountType: string, tresholdData: any = null): FormGroup {
        let group: FormGroup;


        group = this.formBuilder.group({
            POPNUMCYC: [''],
            PKGPPCYC: [''],
            EXFLISTP: [''],
            CLAWBACK: [''],
            PRCDISC: [''],
            PRCCUT: [''],
            PRCCONFIDDISCCYC: [''],
            REIMBGROSSCYC: [''],
        });


        switch (discountType) {
            case 'EC' : {
                group.addControl('EXCAP', this.formBuilder.control(''));
                if (!this.sectionData['EXCAP']) {
                    this.sectionData['EXCAP'] = [];
                }
                this.sectionData['EXCAP'].push();

                break;
            }

            case 'UC': {
                group.addControl('PSUALU', this.formBuilder.control(''));
                if (!this.sectionData['PSUALU']) {
                    this.sectionData['PSUALU'] = [];
                }
                this.sectionData['PSUALU'].push();


                group.removeControl('PKGPPCYC');
                break;
            }

            case 'FCPP': {
                group.addControl('FCPP', this.formBuilder.control(''));
                if (!this.sectionData['FCPP']) {
                    this.sectionData['FCPP'] = [];
                }
                this.sectionData['FCPP'].push();

                break
            }

            case 'PVA-V' : {
                group.addControl('PVA-V', this.formBuilder.array([]));
                this.generateVolumeTreshold(group, tresholdData);
                break;
            }

            case 'PVA-S' : {
                group.addControl('PVA-S', this.formBuilder.array([]));
                this.generatePriceTrashold(group, tresholdData);
                break;
            }

        }

        return group;
    }

    public generateVolumeTreshold(group: FormGroup, data: any = null) {
        if(data) {
            for(let i = 0 ; i < data.length; i++) {
                const volumeArray: FormArray = <FormArray>group.controls['PVA-V'];
                volumeArray.push(this.formBuilder.group({
                    volumeLimit: [data[i]['S_THRESHOLDS']],
                    discount: [data[i]['PRCCONFIDDISCCYC']]
                }));
            }
        } else {
            const volumeArray: FormArray = <FormArray>group.controls['PVA-V'];
            volumeArray.push(this.formBuilder.group({
                volumeLimit: [''],
                discount: ['']
            }));
        }

    }

    public generatePriceTrashold(group: FormGroup, data: any = null) {

        if(data) {
            for(let i = 0 ; i < data.length; i++) {
                const priceArray: FormArray = <FormArray>group.controls['PVA-S'];
                priceArray.push(this.formBuilder.group({
                    priceLimit: [data[i]['S_THRESHOLDS']],
                    discount: [data[i]['PRCCONFIDDISCCYC']]
                }));
            }
        } else {
            const priceArray: FormArray = <FormArray>group.controls['PVA-S'];
            priceArray.push(this.formBuilder.group({
                priceLimit: [''],
                discount: ['']
            }));
        }
    }

    public removeVolumeTreshold(group: FormGroup, index: number) {
        const volumeArray: FormArray = <FormArray>group.controls['PVA-V'];
        volumeArray.removeAt(index);

        const periods: FormArray = <FormArray>this.simulationInputForm.controls['periods'];
        const periodIndex = periods.controls.indexOf(group);
    }

    public removePriceTrashold(group: FormGroup, index: number) {
        const priceArray: FormArray = <FormArray>group.controls['PVA-S'];
        priceArray.removeAt(index);

        const periods: FormArray = <FormArray>this.simulationInputForm.controls['periods'];
        const periodIndex = periods.controls.indexOf(group);
    }

    private initSectionFormData() {
        let formArray: FormArray = <FormArray>this.simulationInputForm.controls['periods'];

        if (formArray.length > 0) {
            formArray = this.formBuilder.array([]);
        }

        for (let i = 0; i < this.sectionData.numberOfCycles; i++) {

            if((this.sectionData.data['Simulation_type'] === 'PVA-V' || this.sectionData.data['Simulation_type'] === 'PVA-S') && this.sectionData['PRCCONFIDDISCCYC']) {

                let tresholdData = [];

                const PRCCONFIDDISCCYC = this.sectionData['PRCCONFIDDISCCYC'];
                const S_THRESHOLDS = this.sectionData['S_THRESHOLDS'];

                const PRCCONFIDDISCCYCArray = PRCCONFIDDISCCYC[i].split('_');
                const S_THRESHOLDSArray = S_THRESHOLDS[i].split('_');

                for(let y = 0; y < PRCCONFIDDISCCYCArray.length; y++) {

                    const PRCCONFIDDISCCYCObj = PRCCONFIDDISCCYCArray[y];
                    const S_THRESHOLDSObj = S_THRESHOLDSArray[y];

                    tresholdData.push({
                        PRCCONFIDDISCCYC: PRCCONFIDDISCCYCObj,
                        S_THRESHOLDS: S_THRESHOLDSObj
                    });
                }
                formArray.push(this.generateMEAPeriod(this.sectionData.data['Simulation_type'], tresholdData));

            } else {
                formArray.push(this.generateMEAPeriod(this.sectionData.data['Simulation_type']));
            }
        }

        this.simulationInputForm.controls["periods"] = formArray;
    }

    public isAgreementType(type: string) {
        return this.sectionData.data['Simulation_type'].match(type);
    }

    addMeaPeriod() {
        let formArray: FormArray = <FormArray>this.simulationInputForm.controls['periods'];
        formArray.push(this.generateMEAPeriod(this.sectionData.data['Simulation_type']));
        this.sectionData.numberOfCycles++;

    }

    onDeleteClicked(index: number) {
        let formArray: FormArray = <FormArray>this.simulationInputForm.controls['periods'];
        formArray.removeAt(index);
        this.removeFromData(index);
        this.simulationInputForm.updateValueAndValidity();
    }

    private removeFromData(index: number) {
        const keys = Object.keys(this.sectionData);

        for (const key of keys) {
            const field = this.sectionData[key];
            if (isArray(field)) {
                field.splice(index, 1);
            }
        }
        this.sectionData.numberOfCycles--;
    }


}



// WEBPACK FOOTER //
// /home/sboda/Documents/eIRP/MEA_source_code/src/app/components/edit-simulation/edit-simulation.component.ts